import type {
  AssetRequirement,
  ManifestFragment
} from "@/app/integration/contracts";
import { CURRICULUM_VOICE_DEFINITIONS } from "@/audio/curriculum-catalog";
import { toMp3FallbackPath } from "@/audio/resource-paths";
import { LEVELS_BY_DOMAIN } from "@/content/levels-by-domain";
import { DOMAINS, type Domain } from "@/domain";

function addRequirement(
  requirementsByPath: Map<string, { kind: "audio" | "image"; levelIds: Set<string> }>,
  path: string,
  kind: "audio" | "image",
  levelIds: readonly string[]
): void {
  const existing = requirementsByPath.get(path);
  if (existing !== undefined) {
    if (existing.kind !== kind) {
      throw new Error("Conflicting resource kinds for " + path);
    }
    for (const levelId of levelIds) {
      existing.levelIds.add(levelId);
    }
    return;
  }

  requirementsByPath.set(path, {
    kind,
    levelIds: new Set(levelIds)
  });
}

function createDomainManifest(domain: Domain): ManifestFragment {
  const levels = LEVELS_BY_DOMAIN[domain];
  const levelIds = new Set(levels.map(({ id }) => id));
  const requirementsByPath = new Map<
    string,
    { kind: "audio" | "image"; levelIds: Set<string> }
  >();

  for (const level of levels) {
    for (const path of level.assets) {
      const kind = path.startsWith("/audio/") ? "audio" : "image";
      addRequirement(
        requirementsByPath,
        path,
        kind,
        [level.id]
      );
      if (kind === "audio" && path.endsWith(".ogg")) {
        addRequirement(
          requirementsByPath,
          toMp3FallbackPath(path),
          kind,
          [level.id]
        );
      }
    }
  }

  for (const voice of CURRICULUM_VOICE_DEFINITIONS) {
    const matchingLevelIds = voice.levelIds.filter((levelId) =>
      levelIds.has(levelId)
    );
    if (matchingLevelIds.length > 0) {
      addRequirement(
        requirementsByPath,
        voice.path,
        "audio",
        matchingLevelIds
      );
      addRequirement(
        requirementsByPath,
        voice.mp3Path,
        "audio",
        matchingLevelIds
      );
    }
  }

  const requirements: AssetRequirement[] = [...requirementsByPath]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([path, requirement]) => ({
      path,
      kind: requirement.kind,
      levelIds: [...requirement.levelIds].sort()
    }));

  return {
    owner: domain + "-curriculum",
    requirements
  };
}

export const manifestFragments: readonly ManifestFragment[] = DOMAINS.map(
  (domain) => createDomainManifest(domain)
);
